import { Component, OnInit } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBriefcase,
  faBuildingColumns,
  faChildren,
  faFileLines,
} from '@fortawesome/free-solid-svg-icons';
import { SVGIcon, starIcon, starOutlineIcon } from '@progress/kendo-svg-icons';
import { every, includes, some } from 'lodash';
import { filter, takeUntil } from 'rxjs';
import { DOHMH_ROLE_IDS } from 'src/app/modules/ei-transition/constants/dohmh-roles';
import { AbstractComponent } from 'src/app/shared/components/abstract-component';
import { Constants } from 'src/app/shared/constants/constants';
import { Permissions } from 'src/app/shared/enums/permissions';
import { Permission } from 'src/app/shared/models/permission';
import { User } from 'src/app/shared/models/user';
import { UserRole } from 'src/app/shared/models/user-role';
import { AuthStore } from 'src/app/stores/auth.store';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  standalone: false,
})
export class NavigationComponent extends AbstractComponent implements OnInit {
  starSvg: SVGIcon = starIcon;
  starOutlineIcon: SVGIcon = starOutlineIcon;
  currentRole: string;
  itemsLoaded: boolean = false;
  user: User;

  // TODO temporary until ngx-permissions is added
  readonly earlyIntervention: string = 'EI Transition';
  readonly cbst: string = 'CBST';
  readonly reports: string = 'Reports';
  readonly dashboard: string = 'My Caseload';

  readonly waitlist: string = 'WaitList';
  readonly verification: string = 'Parent Portal Requests';
  readonly agency: string = 'Administration';
  readonly kip: string = 'KIP';

  navigationItems: {
    name: string;
    route: string;
    svg: SVGIcon;
    show?: boolean;
  }[] = [
    {
      name: this.dashboard,
      route: '/dashboard',
      svg: this.customFaIcon(faBriefcase),
    },
    {
      name: this.earlyIntervention,
      route: '/ei',
      svg: this.customFaIcon(faChildren),
    },

    {
      name: this.reports,
      route: '/reports/EIAS',
      svg: this.customFaIcon(faFileLines),
    },
    {
      name: this.kip,
      route: '/kip',
      svg: this.customFaIcon(faBuildingColumns),
    },
  ];
  private readonly allNavigationItems: typeof this.navigationItems = this.navigationItems;

  constructor(private authStore: AuthStore) {
    super();
  }

  customFaIcon(icon: IconDefinition): SVGIcon {
    return {
      name: icon.iconName,
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      content: `<path d="${icon.icon[4]}" />`,
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      viewBox: `0 0 ${icon.icon[0]} ${icon.icon[1]}`,
    };
  }

  public ngOnInit(): void {
    this.authStore.UserInfo$.pipe(
      filter((user: User) => !!user && !!user?.selectedRole),
      takeUntil(this.componentDestroyed)
    ).subscribe((user: User) => {
      this.user = user;
      this.navigationItems = this.allNavigationItems.filter(
        (n: { name: string; route: string; show?: boolean }) => {
          if (
            n.name === this.kip &&
            includes(Constants.kipExcludedRoleIds, user.selectedRole.roleId)
          ) {
            return false;
          }

          if (
            includes(Constants.kipRoleIds, user.selectedRole?.roleId) &&
            (n.name === this.kip || n.name === this.dashboard)
          ) {
            return true;
          }

          if (
            every(user.userRoles, (userRole: UserRole) =>
              includes(Constants.kipRoleIds, userRole.roleId)
            )
          ) {
            return false;
          }

          if (
            some(
              user.selectedRole?.userPermissions,
              (x: Permission) => x.permissionId === Permissions.CanViewEIRecords
            ) &&
            n.name === this.earlyIntervention
          ) {
            return true;
          }

          if (
            some(
              user.selectedRole?.userPermissions,
              (x: Permission) =>
                x.permissionId === Permissions.CanViewCBSTRecords
            ) &&
            n.name === this.cbst
          ) {
            return true;
          }

          if (
            some(
              user.selectedRole?.userPermissions,
              (x: Permission) =>
                x.permissionId === Permissions.CanAccessOperationalReports
            ) &&
            n.name === this.reports
          ) {
            return true;
          }

          if (n.name === this.kip && !user?.isMDE) {
            return true;
          }

          if (
            !includes(DOHMH_ROLE_IDS, user.selectedRole?.roleId) &&
            n.name === this.dashboard
          ) {
            return true;
          }
          //
          if (n.name === this.waitlist && user?.isGlobalUser) {
            return true;
          }
          if (n.name === this.agency && (user?.isMDE || user?.isGlobalUser)) {
            return true;
          }
          if (n.name === this.verification && user?.isGlobalUser) {
            return true;
          }
          return false;
        }
      );
    });
  }

  showNavigation(): boolean {
    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  override afterOnDestroy(): void {}
}
